import {Alert, AlertTitle, CardContent, Grid, Stack} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	ConfirmButton,
	EditForm,
	Instant,
	Now,
	PlainDateTime,
	PlainDateTimePicker,
	TextField,
	tryParseInstant,
	ViewHeader,
} from "@variocube/app-ui";
import React, {useEffect, useMemo, useState} from "react";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {useBookingShareApi} from "../../../api/bookingShare";
import EmailInput from "../../../controls/EmailInput";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useTenantFeature, useTenantId} from "../../../tenant";
import {UserTimeZoneId} from "../../../utils/date";
import {queryKeys} from "../../../utils/queryKeys";
import {useNumericParam} from "../../../utils/useParam";
import {View} from "../../View";

export function BookingShareEdit() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const tenantId = useTenantId();
	const bookingId = useNumericParam("bookingId");
	const bookingShareId = useNumericParam("bookingShareId");
	const {updateBookingShare, deleteBookingShare, getBookingShare} = useBookingShareApi();

	const canShowBookingShares = useTenantFeature("SHOW_BOOKING_SHARES");

	const [name, setName] = useState<string>("");
	const [validUntil, setValidUntil] = useState<PlainDateTime | null>(null);

	const {isLoading: isBookingShareLoading, data: bookingShare} = useQuery({
		queryKey: queryKeys.bookingShare(tenantId, bookingId, bookingShareId),
		queryFn: () => getBookingShare(bookingShareId, bookingId),
	});

	useEffect(() => {
		if (bookingShare) {
			setName(bookingShare.name);
			setValidUntil(
				tryParseInstant(bookingShare.validUntil)?.toZonedDateTimeISO(UserTimeZoneId)
					.toPlainDateTime() ?? null,
			);
		}
	}, [bookingShare]);

	const onMutationSuccess = async () => {
		await queryClient.invalidateQueries({
			queryKey: queryKeys.bookingShares.byBooking(tenantId, bookingId),
		});
		await queryClient.invalidateQueries({
			queryKey: queryKeys.bookingShares.count(tenantId, bookingId),
		});
		navigate(`../${bookingId}?tab=bookingShares`);
	};

	const {isLoading: isUpdateLoading, mutateAsync: handleUpdate} = useMutation({
		mutationFn: () =>
			updateBookingShare(bookingId, bookingShareId, {
				name: name,
				validUntil: validUntil?.toZonedDateTime(UserTimeZoneId)?.toInstant()?.toString(),
			}),
		onSuccess: onMutationSuccess,
	});

	const {isLoading: isDeactivationLoading, mutateAsync: handleDeactivation} = useMutation({
		mutationFn: () =>
			updateBookingShare(bookingId, bookingShareId, {
				name: bookingShare?.name ?? "",
				validUntil: Now.instant().toString(),
			}),
		onSuccess: onMutationSuccess,
	});

	const {isLoading: isDeleteLoading, mutateAsync: handleDelete} = useMutation({
		mutationFn: () => deleteBookingShare(bookingId, bookingShareId),
		onSuccess: onMutationSuccess,
	});

	const isExpired = useMemo(() => {
		if (!bookingShare) {
			return null;
		}
		const parsedValidUntil = tryParseInstant(bookingShare?.validUntil);
		return parsedValidUntil ? Instant.compare(parsedValidUntil, Now.instant()) < 0 : false;
	}, [bookingShare?.validUntil]) ?? true;

	const isLoading = isBookingShareLoading || isUpdateLoading || isDeleteLoading || isDeactivationLoading;

	if (canShowBookingShares === false) {
		return <Navigate to={".."} />;
	}

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={"../../../.."} relative="path">
					{t(`bookings.title.${wordingFlavor}`)}
				</BreadcrumbLink>
				<BreadcrumbLink component={Link} to={"../../..?tab=bookingShares"} relative="path">
					{bookingId}
				</BreadcrumbLink>
				<BreadcrumbItem>{t(`bookings.shares.update.${wordingFlavor}`)}</BreadcrumbItem>
			</Breadcrumbs>

			<ViewHeader
				title={t(`bookings.shares.update.${wordingFlavor}`)}
				actions={!isExpired && (
					<Stack direction="row" spacing={2}>
						<ConfirmButton
							variant="outlined"
							cancel={t("cancel")}
							title={t("bookings.shares.deactivate")}
							onConfirm={handleDeactivation}
						>
							{t(`bookings.shares.deactivateDescription.${wordingFlavor}`)}
						</ConfirmButton>
					</Stack>
				)}
			/>
			<EditForm
				loading={isLoading || isExpired}
				onSave={handleUpdate}
				labels={t}
			>
				<CardContent>
					<Grid container spacing={2}>
						<Grid item container>
							<Grid item xs={12} sm={6}>
								<TextField
									label={t("bookings.shares.name")}
									value={name}
									onChange={setName}
									fullWidth
									required
									disabled={isExpired}
									inputProps={{
										maxLength: 128,
									}}
									helperText={t("bookings.shares.maxLengthName")}
								/>
							</Grid>
						</Grid>
						<Grid item container>
							<Grid item xs={12} sm={6}>
								<PlainDateTimePicker
									label={t("bookings.shares.validUntil")}
									value={validUntil}
									onChange={setValidUntil}
									fullWidth
									disabled={isExpired}
								/>
							</Grid>
						</Grid>

						{bookingShare?.emails && bookingShare.emails.length > 0 && (
							<Grid item container>
								<Grid item xs={12} sm={6}>
									<EmailInput
										emails={bookingShare.emails}
										readonly={true}
									/>
								</Grid>
							</Grid>
						)}
					</Grid>
				</CardContent>
			</EditForm>

			<Alert
				severity="error"
				action={
					<ConfirmButton
						cancel={t("cancel")}
						title={t(`bookings.shares.delete.title.${wordingFlavor}`)}
						onConfirm={handleDelete}
						color="error"
					>
						{t(`bookings.shares.delete.confirm.${wordingFlavor}`)}
					</ConfirmButton>
				}
			>
				<AlertTitle>
					{t(`bookings.shares.delete.title.${wordingFlavor}`)}
				</AlertTitle>
				{t(`bookings.shares.delete.message.${wordingFlavor}`)}
			</Alert>
		</View>
	);
}
